const topics = [
  {
    id: "overview",
    title: "Taleem administration",
    summary: "Understand how departments, classrooms, academic periods, curriculum, grades, and report cards work together.",
    keywords: ["administration", "setup", "school", "overview"],
    routeNames: ["home"],
    sections: [
      {
        title: "Recommended setup order",
        steps: [
          "Create departments for the programs your institute teaches.",
          "Create subjects, then combine them into curriculums.",
          "Create physical classrooms and optional virtual classrooms.",
          "Create academic years and their terms.",
          "Assign teachers, students, and curriculums before entering grades.",
        ],
      },
      {
        title: "The main distinction",
        body: [
          "Departments, classrooms, subjects, and curriculums are reusable definitions. Years and terms describe when teaching and assessment happened.",
          "Report cards preserve the students, grades, ranking group, and document for a particular academic period.",
        ],
      },
    ],
    related: ["departments", "classrooms", "virtual-classrooms", "report-cards"],
  },
  {
    id: "teachers",
    title: "Teachers",
    summary: "Teachers are staff members who can be assigned to the physical classrooms they teach.",
    keywords: ["teacher", "staff", "assignment", "classroom"],
    routeNames: ["teachers", "teacher-list"],
    sections: [
      {
        title: "What a teacher record does",
        body: ["A teacher record identifies the staff member and controls which classrooms and teacher-facing features they can access."],
        bullets: [
          "Assign teachers to physical classrooms where they teach students.",
          "A teacher assignment does not determine a student's academic year or report-card cohort.",
          "Access should be limited to the departments and classrooms the teacher is responsible for.",
        ],
      },
    ],
    related: ["departments", "classrooms", "grades"],
  },
  {
    id: "students",
    title: "Students",
    summary: "Student records connect a child to a parent, department, classroom, tuition, and academic history.",
    keywords: ["student", "child", "parent", "enrollment", "tuition", "report card"],
    routeNames: ["student", "student-list"],
    sections: [
      {
        title: "What a student record represents",
        bullets: [
          "The parent is the financially responsible account for the student.",
          "The department controls the student's program rules and department-based fees.",
          "The physical classroom identifies the student's regular teaching group and assigned teachers.",
          "Academic years, terms, curriculums, grades, and report cards preserve the student's academic progress.",
        ],
      },
      {
        title: "Enrollment and fees",
        body: [
          "A student's active enrollment and department determine which tuition rules apply. The department can define a default monthly fee, selected billing months, sibling fee tiers, advance collection, and an annual student charge.",
          "Invoices and payments belong to the parent account, while invoice lines identify the students and programs being charged.",
        ],
        note: "Changing a department, fee, or enrollment can affect future billing. Review the parent profile and billing preview before making a financial change.",
      },
      {
        title: "Academic management",
        bullets: [
          "Assign the student to the correct classroom when the department requires one.",
          "Use the classroom curriculum for subject and grade entry.",
          "Choose the correct academic year and term when entering grades or generating report cards.",
          "Use saved report-card versions for historical records instead of relying on today's classroom membership.",
        ],
      },
    ],
    related: ["parents", "departments", "classrooms", "report-cards"],
  },
  {
    id: "parents",
    title: "Parents",
    summary: "A parent is the responsible family account that connects students, invoices, payments, credits, and recurring billing.",
    keywords: ["parent", "guardian", "family", "responsible", "billing", "balance"],
    routeNames: ["payment-customer-list", "deleted-customer-list"],
    sections: [
      {
        title: "What belongs to a parent",
        bullets: [
          "One or more current or former students",
          "Contact information, internal notes, files, and payment methods",
          "Invoices issued for tuition or other charges",
          "Payments and the invoices where each payment was applied",
          "Advance tuition credit and recurring subscription templates",
        ],
      },
      {
        title: "Financial responsibility",
        body: [
          "Student charges are grouped under the responsible parent's account. The parent's balance is the unpaid portion of open invoices after payments, advance credit, approved charity support, payroll deductions, and other valid adjustments are applied.",
          "A saved payment method can be used for automatic collection when the institute's billing settings allow it. Marking a parent as a cash payer records the family's normal payment preference.",
        ],
      },
    ],
    related: ["students", "parent-overview", "parent-invoices", "parent-subscriptions"],
  },
  {
    id: "parent-overview",
    title: "Parent profile overview",
    summary: "Review the family account, balance, payment method, billing preview, internal notes, and communication history.",
    keywords: ["parent profile", "overview", "balance", "payment method", "billing preview", "note"],
    routeNames: ["customer-profile"],
    sections: [
      {
        title: "What the profile header shows",
        bullets: [
          "The current family balance and available advance-payment credit",
          "The parent contact details and preferred payment methods",
          "Tuition amounts summarized from the family's active students",
          "Actions for billing preview, statements, re-enrollment, and parent maintenance when permitted",
        ],
      },
      {
        title: "Overview tab",
        body: ["Use this tab to manage payment methods, the family's internal profile note, important-note alerts, cash-payment preference, and staff comments."],
      },
      {
        title: "Before collecting tuition",
        steps: [
          "Confirm that every active student is in the correct department and classroom.",
          "Review the department fee rules and the family's payment method.",
          "Open Preview billing to inspect the expected charges before an invoice or collection run.",
          "Use the Invoices and Payments tabs to verify the resulting balance and allocations.",
        ],
      },
    ],
    related: ["parents", "parent-students", "parent-invoices", "parent-payments"],
  },
  {
    id: "parent-students",
    title: "Students on a parent profile",
    summary: "This tab lists the students for whom this parent is responsible, including former students when available.",
    keywords: ["parent students", "children", "enrollment", "former student", "family"],
    routeNames: [],
    sections: [
      {
        title: "How this list is used",
        bullets: [
          "Open a student to review enrollment, department, classroom, and academic information.",
          "Confirm that the correct parent is responsible for the student before billing.",
          "Former students remain visible for historical invoices, payments, and academic records.",
          "The student's department and active enrollment drive department-based tuition rules.",
        ],
      },
    ],
    related: ["students", "parent-invoices", "departments", "classrooms"],
  },
  {
    id: "parent-invoices",
    title: "Parent invoices",
    summary: "Invoices record what the family owes, why it was charged, what has been paid, and the remaining balance.",
    keywords: ["invoice", "tuition", "charge", "balance", "automatic billing", "department fee"],
    routeNames: ["invoice-list", "missing-invoice-list"],
    sections: [
      {
        title: "How invoices are created",
        bullets: [
          "Department monthly billing can add a tuition line for each active student in the enabled billing months.",
          "A department annual charge can create one open parent invoice each year with one line per eligible active student.",
          "A subscription template can generate future invoices on its configured schedule.",
          "Authorized staff can create a parent invoice manually for an exceptional charge or correction.",
        ],
      },
      {
        title: "Department-based tuition",
        body: [
          "The student's department supplies the default monthly fee and the months that should be billed. Sibling fee tiers may reduce the later students' tuition when multiple active siblings are enrolled in the same department.",
          "The institute settings supply the billing day, automatic-billing status, collection start date, and payment routing. Saving a department plan does not create or collect invoices while automatic billing is paused.",
        ],
      },
      {
        title: "What happens after creation",
        body: [
          "Available advance tuition credit can be applied to the invoice. Approved charity support and payroll deductions are recorded separately so they reduce the parent responsibility without appearing as parent cash or a card charge.",
          "Automatic card collection charges only the remaining parent balance and follows the institute's collection safeguards. The invoice stays available for review even when collection is manual or unsuccessful.",
        ],
      },
      {
        title: "Reading the list",
        bullets: [
          "Open means an unpaid balance remains.",
          "Paid means valid payments or credits cover the invoice balance.",
          "Open an invoice to review its student or fee lines and every payment allocation.",
          "Use the Payments tab to inspect receipts; do not treat an invoice itself as proof that money was received.",
        ],
      },
    ],
    related: ["departments", "parent-payments", "parent-advance-payments", "parent-subscriptions"],
  },
  {
    id: "parent-payments",
    title: "Parent payments",
    summary: "Payments record money or approved internal credits received and show which invoices they satisfy.",
    keywords: ["payment", "receipt", "allocation", "card", "cash", "refund"],
    routeNames: [],
    sections: [
      {
        title: "Payment history",
        bullets: [
          "Expand a payment to see the invoices and amounts to which it was applied.",
          "Provider labels identify card processing when available; manual or cash payments are recorded by authorized staff.",
          "A payment can cover one or more invoices, and an invoice can receive more than one payment.",
          "Refund actions affect the payment record and should be used only after verifying the processor and invoice impact.",
        ],
      },
      {
        title: "Payment versus invoice",
        body: ["An invoice is a charge. A payment is value received or credited against that charge. Always inspect the allocation details when reconciling a parent's balance."],
      },
    ],
    related: ["parent-invoices", "parent-advance-payments", "parent-overview"],
  },
  {
    id: "sadaqah-bank-transfers",
    title: "Sadaqah bank transfers",
    summary: "Create and verify exact transfer batches between the Sadaqah and tuition bank accounts.",
    keywords: ["sadaqah", "charity", "masjid responsibility", "temporary support", "permanent support", "bank transfer", "batch"],
    routeNames: ["sadaqah-recoveries"],
    sections: [
      {
        title: "Temporary and permanent support",
        body: [
          "Temporary support keeps the parent balance due. Money first moves from Sadaqah to tuition, and a later parent payment creates an amount that must be returned from tuition to Sadaqah.",
          "Permanent support covers approved Masjid or charity responsibility and is not expected back from the parent. It only requires the Sadaqah-to-tuition bank transfer.",
        ],
      },
      {
        title: "Create and complete a batch",
        steps: [
          "Select the exact invoices whose total matches one bank transaction.",
          "Create the batch. This groups the items under Waiting to move but does not record that money moved.",
          "Complete the real bank transfer, then click Mark bank transfer moved on that batch.",
          "Enter the exact bank date and a reference or note. The batch then appears in Moved history.",
        ],
      },
      {
        title: "What happens to later invoices",
        body: [
          "Creating or completing one batch does not close the selected month. If more eligible support is recorded later, those invoices appear as Needs batch and can be grouped into another bank transaction.",
          "Each batch should represent exactly one transaction on the bank statement. Use Edit batch to correct its invoices, date, reference, or note when necessary.",
        ],
        note: "The batch creation date and the actual bank-transfer date are different. The actual date is recorded only when the batch is marked moved.",
      },
    ],
    related: ["parent-invoices", "parent-payments"],
  },
  {
    id: "parent-advance-payments",
    title: "Parent advance payments",
    summary: "Advance payments hold tuition credit received before it is fully applied to invoices.",
    keywords: ["advance payment", "credit", "prepaid", "available tuition credit", "allocation"],
    routeNames: ["advance-payment", "advance-payment-list"],
    sections: [
      {
        title: "How advance credit works",
        body: [
          "The original amount records what was received. The available balance is the portion that has not yet been attached to invoices.",
          "When eligible invoices are created, advance credit can be applied before collecting the remaining parent balance. Staff can also attach available credit to an invoice from this tab.",
        ],
      },
      {
        title: "Important distinction",
        body: ["Advance credit is not a new invoice and is not the same as an ordinary payment already allocated to an invoice. Its allocation history shows where the prepaid amount was used."],
      },
    ],
    related: ["parent-invoices", "parent-payments", "parent-subscriptions"],
  },
  {
    id: "parent-subscriptions",
    title: "Parent subscriptions",
    summary: "A subscription is a recurring billing template that creates future invoices; it is not itself an invoice or payment.",
    keywords: ["subscription", "recurring", "template", "next invoice", "automatic"],
    routeNames: ["subscription", "subscription-list"],
    sections: [
      {
        title: "What a subscription controls",
        bullets: [
          "The recurring invoice amount and description",
          "The start, duration, and next invoice date",
          "The parent or donor responsible for the recurring charge",
          "The billing or collection method configured for the template",
        ],
      },
      {
        title: "Invoice creation and collection",
        body: [
          "When a template reaches its next invoice date, it can generate the next invoice automatically through the billing process or be run by authorized staff using Create Invoice.",
          "Create Invoice & Collect also attempts collection using the configured payment information. Creating an invoice and receiving payment are separate events, so confirm both in the Invoices and Payments tabs.",
        ],
      },
      {
        title: "Department billing versus subscriptions",
        body: ["Use department billing for tuition driven by each student's department and enrollment. Use a subscription for a specifically configured recurring template. Review both to avoid charging the same purpose twice."],
      },
    ],
    related: ["parent-invoices", "parent-payments", "departments"],
  },
  {
    id: "parent-files",
    title: "Parent files",
    summary: "Files store documents attached to this family account for authorized staff reference.",
    keywords: ["parent files", "attachment", "document", "upload"],
    routeNames: [],
    sections: [
      {
        title: "Using parent files",
        bullets: [
          "Attach documents that belong to the family account rather than a single invoice or student record.",
          "Use a clear file name so other authorized staff can identify the document.",
          "Follow your institute's privacy policy when uploading, viewing, or removing personal documents.",
        ],
      },
    ],
    related: ["parents", "parent-overview", "parent-students"],
  },
  {
    id: "departments",
    title: "Departments",
    summary: "A department is a stable program that brings together enrollment, tuition schedules, report cards, applications, and communication settings.",
    keywords: ["department", "program", "maktab", "hifz", "alim", "fee", "application", "custom question"],
    routeNames: ["departments", "classes-list", "classes-create", "classes-edit"],
    sections: [
      {
        title: "How departments are used",
        bullets: [
          "Students enroll in a department or program.",
          "Physical and virtual classrooms are connected to a department.",
          "Academic years, fees, curriculums, and reporting can be organized by department.",
        ],
      },
      {
        title: "Enrollment, schedule, and fees",
        bullets: [
          "Set the standard monthly fee and choose the months in which department tuition is billed.",
          "Choose whether students require a classroom assignment and which weekdays the department operates.",
          "Optional sibling tiers set the monthly amount for the second and later active siblings in this department.",
          "Collect Fees in Advance, Count In Subscription, and Kids Class Program adjust how this department participates in institute workflows.",
          "An annual student charge can create one yearly parent invoice with a line for each eligible active student.",
        ],
        note: "Department billing supplies the student fee rules. The institute settings control the billing day, automatic-billing status, collection start date, and payment routing.",
      },
      {
        title: "Report cards",
        body: ["Department report-card settings control calculations and the information displayed for students in this program."],
        bullets: [
          "Final averages, automated remarks, attendance, percentages, and grade guides",
          "Feedback display mode and teacher or supervisor signature labels",
          "The principal signature used on the report",
          "Department-specific grade ranges, with system defaults used when no ranges are entered",
        ],
      },
      {
        title: "Applications and communication",
        bullets: [
          "Custom questions collect department-specific answers during the application process.",
          "The public application link opens an application directly for this department.",
          "Application contact information tells applicants how to reach the program.",
          "The department description supplies public-facing program details.",
          "Pending-balance letter content supports department-specific family communication.",
        ],
      },
      {
        title: "Do not recreate departments each year",
        body: ["Keep the department in place. Use academic years and terms to separate one period from another."],
      },
    ],
    related: ["students", "parent-invoices", "classrooms", "report-cards"],
  },
  {
    id: "classrooms",
    title: "Physical classrooms",
    summary: "A classroom is the student's regular teaching group. The classroom remains in place while students may move over time.",
    keywords: ["classroom", "physical", "student", "transfer", "room"],
    routeNames: ["classRoom", "class-room-list"],
    sections: [
      {
        title: "What belongs to a classroom",
        bullets: [
          "A department",
          "Assigned teachers",
          "Current students",
          "One or more curriculums",
        ],
      },
      {
        title: "When students move",
        body: [
          "Move the student to the new classroom instead of renaming or replacing the classroom itself.",
          "Historical report cohorts preserve the students who belonged to an earlier report period.",
        ],
      },
    ],
    related: ["departments", "virtual-classrooms", "academic-years", "report-cards"],
  },
  {
    id: "virtual-classrooms",
    title: "Virtual classrooms",
    summary: "A virtual classroom is a temporary assessment or comparison group. It does not replace the student's physical classroom.",
    keywords: ["virtual classroom", "comparison", "ranking", "qaida", "quran", "hifz", "level"],
    routeNames: ["virtual-class-room-list"],
    sections: [
      {
        title: "When to use one",
        body: ["Use a virtual classroom when students from different physical classrooms should be graded or ranked with students at a comparable level."],
        bullets: [
          "Qaida students compared with other Qaida students",
          "Quran readers compared with Quran readers",
          "Hifz students grouped by comparable para or assessment level",
          "Any department or curriculum that needs a temporary comparison group",
        ],
      },
      {
        title: "Starting a new year or term",
        steps: [
          "Make sure the previous period's report has been generated so its cohort is frozen.",
          "Keep the virtual classroom when its meaning is still the same, such as Para 1–10.",
          "Remove students who are no longer in the current group and add the new students.",
          "When generating reports, select the new academic year and exactly one term.",
          "Taleem creates a separate frozen cohort for that year and term.",
        ],
      },
      {
        title: "Why old reports remain correct",
        body: [
          "After a report cohort is frozen, changing the current virtual-classroom students does not change that older cohort.",
          "Selecting the older year and term loads its frozen students, rankings, and report versions. Selecting the new period uses the new period's cohort.",
        ],
        note: "If no report cohort or saved report ever existed for an older period, Taleem cannot reconstruct that old roster from the current students. Finalize each period before replacing its membership.",
      },
    ],
    related: ["classrooms", "academic-years", "terms", "curriculums", "report-cards"],
  },
  {
    id: "academic-years",
    title: "Academic years",
    summary: "An academic year defines the reporting window. It does not own or duplicate the permanent classroom.",
    keywords: ["year", "academic year", "report window", "archive"],
    routeNames: ["year", "year-list", "year-calendar"],
    sections: [
      {
        title: "How years work",
        bullets: [
          "Create a year with its start date, end date, and report-card grace period.",
          "Create terms inside the year.",
          "Choose the year when entering grades or generating reports.",
          "Older years lock after their configured reporting window.",
        ],
      },
      {
        title: "Historical corrections",
        body: ["An administrator may temporarily reopen a locked year. A reason is required, and a corrected report is saved as a new version."],
      },
    ],
    related: ["terms", "classrooms", "virtual-classrooms", "report-cards"],
  },
  {
    id: "terms",
    title: "Terms",
    summary: "A term is a grading or assessment period inside an academic year.",
    keywords: ["term", "semester", "quarter", "assessment", "period"],
    routeNames: ["terms", "terms-list"],
    sections: [
      {
        title: "What terms control",
        bullets: [
          "When grades and comments are entered",
          "Which assessment period appears on a report card",
          "Which students belong to a virtual comparison cohort",
          "When grade entry is completed, locked, or temporarily reopened",
        ],
      },
      {
        title: "Virtual classroom reports",
        body: ["A virtual-classroom report uses exactly one term so students who moved levels between terms are never mixed in the same ranking cohort."],
      },
    ],
    related: ["academic-years", "grades", "virtual-classrooms", "report-cards"],
  },
  {
    id: "subjects",
    title: "Subjects",
    summary: "A subject is one teachable and gradeable item, such as Qaida, Quran Reading, Tajweed, Fiqh, or Islamic Studies.",
    keywords: ["subject", "course", "grade", "tajweed", "fiqh"],
    routeNames: ["subject", "subject-list"],
    sections: [
      {
        title: "Subject versus curriculum",
        body: ["Create each subject once. A curriculum then selects the subjects that belong together and may configure their marks or weight."],
      },
      {
        title: "Example",
        body: ["The subjects Quran Reading, Tajweed, and Islamic Studies can be combined into a curriculum named Maktab Level 3."],
      },
    ],
    related: ["curriculums", "grades", "classrooms"],
  },
  {
    id: "curriculums",
    title: "Curriculums",
    summary: "A curriculum is an organized collection of subjects used by a physical or virtual classroom.",
    keywords: ["curriculum", "subjects", "marks", "weight", "classroom"],
    routeNames: ["curriculums", "curriculum-list"],
    sections: [
      {
        title: "How to build a curriculum",
        steps: [
          "Create the individual subjects first.",
          "Create a curriculum and add the relevant subjects.",
          "Configure marks or weighting where required.",
          "Assign the curriculum to the physical or virtual classroom that uses it.",
        ],
      },
      {
        title: "Why it matters",
        body: ["The assigned curriculum determines which subjects are available for grading and which results appear on the report card."],
      },
    ],
    related: ["subjects", "classrooms", "virtual-classrooms", "grades"],
  },
  {
    id: "grades",
    title: "Grades",
    summary: "Grades connect a student, subject, curriculum, classroom, year, and term.",
    keywords: ["grade", "marks", "percentage", "correction", "student"],
    routeNames: ["grades", "grade-list"],
    sections: [
      {
        title: "Entering grades",
        steps: [
          "Select the department or classroom context.",
          "Select the academic year and term.",
          "Select the student and enter marks for the curriculum subjects.",
          "Decimal marks and percentages may be entered when required.",
        ],
      },
      {
        title: "Correcting an old grade",
        body: ["After an administrator reopens a historical term, Taleem loads the frozen historical roster and requires a correction reason. The grade change is recorded in the audit history."],
      },
    ],
    related: ["subjects", "curriculums", "terms", "report-cards"],
  },
  {
    id: "report-cards",
    title: "Report cards and versions",
    summary: "Report cards preserve the selected period, students, grades, subjects, rankings, and generated document.",
    keywords: ["report card", "archive", "version", "old report", "correction", "ranking"],
    routeNames: ["reportcard-home", "termComments", "term-comments-list"],
    sections: [
      {
        title: "Generating a report",
        steps: [
          "Open the physical or virtual classroom.",
          "Choose the academic year and term or terms.",
          "For a virtual classroom, select exactly one term.",
          "Generate the report to freeze the period's comparison cohort and report snapshot.",
        ],
      },
      {
        title: "Old and corrected reports",
        body: [
          "The newest finalized report is the current version. Older versions remain archived.",
          "Regenerating a reopened historical report requires a reason and creates a new version linked to the previous one. It does not replace the original snapshot or PDF.",
        ],
      },
      {
        title: "Finding an older report",
        body: ["Select the student or classroom, then choose the older academic year and period. Taleem uses the saved report and frozen cohort for that period rather than today's roster."],
      },
    ],
    related: ["grades", "academic-years", "terms", "virtual-classrooms"],
  },
]

export const helpTopics = topics

export function getHelpTopic(id) {
  return topics.find(topic => topic.id === id) || topics[0]
}

export function getHelpTopicForRoute(routeName) {
  return topics.find(topic => (topic.routeNames || []).includes(routeName)) || topics[0]
}
